import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { DragDropService } from '@app/core/services/drag-drop.service';
import { LogEvent, SetEventProperties } from '@app/core/states/event-tracking.actions';
import { capitalizeFirstLetter } from '@app/core/utils';
import { MockTabsService } from '@app/features/app-content/services/mock-tabs.service';
import { AddNewTab } from '@app/features/app-content/states/app-configuration.actions';
import {
  AppConfigurationState,
  UserRadio,
} from '@app/features/app-content/states/app-configuration.state';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Store } from '@ngxs/store';
import { filter, Observable, Subscription, combineLatest } from 'rxjs';

interface PageType {
  type: TabKey;
  name: string;
  icon: IconProp;
}

export type TabKey =
  | 'dedicaces'
  | 'rss'
  | 'rssPodcasts'
  | 'rssVideo'
  | 'rssNews'
  | 'site'
  | 'html'
  | 'livefeed'
  | 'dedicacesaudio'
  | 'reveil'
  | 'podcast'
  | 'ckoi';

@Component({
  selector: 'rk-add-new-page',
  templateUrl: './add-new-page.component.html',
  styleUrls: ['./add-new-page.component.scss'],
  standalone: false,
})
export class AddNewPageComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly mockTabs = inject(MockTabsService);
  private readonly dragDropService = inject(DragDropService);

  userRadios$: Observable<UserRadio[]> = this.store.select(
    AppConfigurationState.userRadios,
  );
  podcastPageIsAvailable$: Observable<boolean> = this.store.select(
    AppConfigurationState.podcastPageIsAvailable,
  );
  songHistoryPageIsAvailable$: Observable<boolean> = this.store.select(
    AppConfigurationState.songHistoryPageIsAvailable,
  );
  hasPodcastPage$: Observable<boolean> = this.store.select(
    AppConfigurationState.hasPodcastPage,
  );
  hasSongHistoryPage$: Observable<boolean> = this.store.select(
    AppConfigurationState.hasSongHistoryPage,
  );

  private readonly basePageTypes: PageType[] = [
    { type: 'dedicaces', name: marker('add-new-page.dedications'), icon: 'quote-right' },
    {
      type: 'dedicacesaudio',
      name: marker('add-new-page.audio-dedications'),
      icon: 'microphone',
    },
    { type: 'site', name: marker('add-new-page.site'), icon: 'globe-americas' },
    { type: 'livefeed', name: marker('add-new-page.livefeed'), icon: 'comments' },
    { type: 'rss', name: marker('add-new-page.rss'), icon: 'rss' },
    { type: 'rssVideo', name: marker('add-new-page.rss-video'), icon: 'video' },
    { type: 'rssNews', name: marker('add-new-page.rss-news'), icon: 'newspaper' },
    { type: 'html', name: marker('add-new-page.html'), icon: 'code' },
    { type: 'reveil', name: marker('add-new-page.reveil'), icon: 'clock' },
  ];

  pageTypes: PageType[] = [];

  isContentVisible = true;
  private readonly subscriptions = new Subscription();
  bodyElement: HTMLElement = document.body;

  ngOnInit(): void {
    this.subscriptions.add(
      this.userRadios$.pipe(filter(radios => !!radios)).subscribe(radios => {
        this.mockTabs.getTabs().dedicacesaudio.idradio = radios[0].idradio;
      }),
    );

    // set up page types based on podcast and song history availability
    this.subscriptions.add(
      combineLatest([
        this.podcastPageIsAvailable$,
        this.hasPodcastPage$,
        this.songHistoryPageIsAvailable$,
        this.hasSongHistoryPage$,
      ]).subscribe(([isPodcastAvailable, hasPodcast, isSongHistoryAvailable, hasSongHistory]) => {
        const list: PageType[] = [...this.basePageTypes];

        // podcast logic
        if (isPodcastAvailable) {
          // if the podcast module is available and there is no existing podcast page, show the podcast option
          if (!hasPodcast) {
            list.push({
              type: 'podcast',
              name: marker('add-new-page.podcast'),
              icon: 'podcast',
            });
          }
        } else {
          // if the podcast module is not available, show rss podcasts option
          list.push({
            type: 'rssPodcasts',
            name: marker('add-new-page.rss-podcasts'),
            icon: 'podcast',
          });
        }

        // song history logic
        if (isSongHistoryAvailable && !hasSongHistory) {
          // if the song history module is available and there is no existing song history page, show the song history option
          list.push({
            type: 'ckoi',
            name: marker('add-new-page.song-history'),
            icon: 'calendar',
          });
        }

        this.pageTypes = list;
      }),
    );
  }

  toggleContent(): void {
    this.isContentVisible = !this.isContentVisible;
  }

  addNewPage(tabType: TabKey) {
    const newTabs = this.mockTabs.getTabs();
    this.store.dispatch(new AddNewTab(newTabs[tabType]));
    // event tracking
    this.store.dispatch(
      new SetEventProperties({
        name: 'ButtonAddPage' + capitalizeFirstLetter(tabType),
        component: 'Button',
      }),
    );
    this.store.dispatch(new LogEvent('Button Clicked'));
  }

  dragStart(): void {
    this.dragDropService.startDragging();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
